import React, { useState, useEffect, useRef } from 'react';
import { IonInput, IonSelect, IonSelectOption } from '@ionic/react';
import { SPLIT_TYPE_OPTIONS } from '../utils/ticketConstants';

interface InlineEditProps {
  type: 'text' | 'number' | 'select';
  value: string | number;
  options?: { id: string; label: string }[];
  onSave: (value: string | number) => void;
  onCancel?: () => void;
  placeholder?: string;
  className?: string;
  selectLabel?: string;
  isPrice?: boolean;
}

const InlineEdit: React.FC<InlineEditProps> = ({
  type,
  value,
  options,
  onSave,
  onCancel,
  placeholder,
  className = '',
  selectLabel,
  isPrice = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const inputRef = useRef<HTMLIonInputElement>(null);
  const selectRef = useRef<HTMLIonSelectElement>(null);

  useEffect(() => {
    if (isEditing && type !== 'select') {
      setTimeout(() => {
        inputRef.current?.setFocus();
      }, 100);
    }
  }, [isEditing, type]);

  const handleSave = () => {
    if (editValue !== value) {
      onSave(editValue);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(value);
    setIsEditing(false);
    onCancel?.();
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLIonInputElement>) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  const handleBlur = () => {
    handleSave();
  };

  const displayValue = () => {
    if (type === 'select' && options) {
      const option = options.find(opt => opt.id === value);
      return option ? option.label : value;
    }
    if (type === 'number' && isPrice) {
      const numValue = Number(value);
      return isNaN(numValue) ? value : numValue.toFixed(2);
    }
    return value;
  };

  if (isEditing) {
    if (type === 'select') {
      return (
        <IonSelect
          ref={selectRef}
          value={editValue}
          placeholder={selectLabel || 'Select...'}
          onIonChange={(e) => {
            setEditValue(e.detail.value);
            onSave(e.detail.value);
            setIsEditing(false);
          }}
          onIonCancel={handleCancel}
          onClick={(e) => e.stopPropagation()}
          className={`inline-edit-select ${className}`}
          style={{ minWidth: '120px' }}
        >
          {options?.map((option) => (
            <IonSelectOption key={option.id} value={option.id} onClick={(e) => e.stopPropagation()}>
              {option.label}
            </IonSelectOption>
          ))}
        </IonSelect>
      );
    }

    return (
      <IonInput
        ref={inputRef}
        type={type}
        value={editValue}
        placeholder={placeholder}
        onIonInput={(e) => setEditValue(e.detail.value!)}
        onKeyDown={handleKeyPress}
        onIonBlur={handleBlur}
        onClick={(e) => e.stopPropagation()}
        step={type === 'number' ? '0.01' : undefined}
        className={`inline-edit-input ${className}`}
        style={{ 
          minWidth: type === 'number' ? '80px' : '120px',
          border: '1px solid #ccc',
          borderRadius: '4px',
          padding: '4px 8px'
        }}
      />
    );
  }

  return (
    <span
      onClick={(e) => {
        e.stopPropagation();
        setIsEditing(true);
      }}
      className={`inline-edit-display cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 p-1 rounded ${className}`}
      title="Click to edit"
    >
      {displayValue()}
    </span>
  );
};

export default InlineEdit;